import { driver } from "driver.js";
import type { DriveStep } from "driver.js";
import { DRIVER_GERMAN_CONFIG } from "@/lib/tours/driverConfig";
import { getTourDefinition } from "@/lib/tours/registry";
import { SHELL_TOUR_STEPS } from "@/lib/tours/shellTour";
import { filterVisibleSteps } from "@/lib/tours/stepUtils";
import { markTourSeen } from "@/lib/tours/tourStorage";
import type { TourId } from "@/lib/tours/types";

let activeDriver: ReturnType<typeof driver> | null = null;

function destroyActiveDriver(): void {
  if (activeDriver) {
    activeDriver.destroy();
    activeDriver = null;
  }
}

function runTour(steps: DriveStep[], onComplete?: () => void): boolean {
  const visibleSteps = filterVisibleSteps(steps);
  if (visibleSteps.length === 0) return false;

  destroyActiveDriver();

  const driverObj = driver({
    ...DRIVER_GERMAN_CONFIG,
    steps: visibleSteps,
    onDestroyed: () => {
      activeDriver = null;
      onComplete?.();
    },
  });

  activeDriver = driverObj;
  driverObj.drive();
  return true;
}

/** Startet die Tour für eine Seite; beim Dashboard wird optional zuerst die App-Übersicht gezeigt. */
export function startTour(tourId: TourId, options?: { includeShell?: boolean; force?: boolean }): boolean {
  void options?.force;
  const definition = getTourDefinition(tourId);
  const includeShell = options?.includeShell ?? tourId === "dashboard";

  const pageSteps = definition.steps;
  const steps: DriveStep[] =
    includeShell && tourId !== "shell"
      ? [...filterVisibleSteps(SHELL_TOUR_STEPS), ...pageSteps]
      : pageSteps;

  const tourIdsToMark: TourId[] = includeShell && tourId !== "shell" ? ["shell", tourId] : [tourId];

  return runTour(steps, () => {
    for (const id of tourIdsToMark) markTourSeen(id);
  });
}

export function startShellTour(): boolean {
  return runTour(SHELL_TOUR_STEPS, () => markTourSeen("shell"));
}

export function stopTour(): void {
  destroyActiveDriver();
}
